/**
 * ============================================================
 * 用户管理页面 (Users) - v9
 * ============================================================
 * 管理员可操作编辑用户
 * 企业用户必填：企业名称、对公账户、开户行名称、联系人姓名、电话
 * 个人用户必填：姓名、银行卡号、开户行、手机号码、身份证号
 * 可绑定招募经纪人和运营经纪人
 * ============================================================
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { userApi, brokerApi, roleApi } from '../api';
import { useAuth } from '../auth';
import type { User, Broker, Role } from '../types';
import {
  UserCog, Plus, Pencil, Trash2, X, RefreshCw, Shield,
  UserCheck, Search, Building2, User as UserIcon, CreditCard, Landmark,
  Phone, Mail, Lock, IdCard, UsersIcon, Link
} from 'lucide-react';

interface UserForm {
  username: string;
  password: string;
  name: string;
  nameReal: string;
  role: string;
  cardType: '个人' | '企业';
  // 企业
  companyName: string;
  bankAccountPublic: string;
  bankName: string;
  contactName: string;
  contactPhone: string;
  // 个人
  realName: string;
  bankCard: string;
  personalBankName: string;
  mobilePhone: string;
  idCard: string;
  // 经纪人绑定
  brokerId: number;
  brokerName: string;
}

const emptyForm: UserForm = {
  username: '', password: '', name: '', nameReal: '', role: 'broker', cardType: '个人',
  companyName: '', bankAccountPublic: '', bankName: '', contactName: '', contactPhone: '',
  realName: '', bankCard: '', personalBankName: '', mobilePhone: '', idCard: '',
  brokerId: 0, brokerName: ''
};

export default function Users() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<UserForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 5000); }, []);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const u = await userApi.list().catch(() => { showError('加载用户列表失败'); return []; });
      // 调试：打印用户类型信息
      console.log('[Users] 用户列表:', u.map(user => ({
        username: user.username,
        card_type: (user as any).card_type,
        card_type_value: JSON.stringify((user as any).card_type)
      })));
      setUsers(u);
    } catch { showError('加载用户列表失败'); }
    try {
      const b = await brokerApi.list().catch(() => { return []; });
      setBrokers(b);
    } catch { /* ignore */ }
    try {
      const r = await roleApi.list().catch(() => { showError('加载角色列表失败'); return []; });
      setRoles(r);
      console.log('[Users] 加载角色列表:', r.length, '个角色', r.map(x => x.name).join(', '));
    } catch { showError('加载角色列表失败'); }
    finally { setLoading(false); }
  };

  const openEdit = (user: User | null) => {
    setEditingUser(user);
    if (user) {
      setEditForm({
        username: user.username || '', password: '',
        name: user.name || '', nameReal: user.name_real || '',
        role: user.role || 'broker', cardType: (user.card_type as '个人' | '企业') || '个人',
        // 企业用户信息
        companyName: user.company_name || '',
        bankAccountPublic: (user as any).bank_account_public || '',
        bankName: user.bank_name || '',
        contactName: user.name_real || '', // 联系人姓名使用name_real
        contactPhone: user.phone || '', // 联系电话使用phone
        // 个人用户信息
        realName: user.name_real || '',
        bankCard: user.bank_card || '',
        personalBankName: user.bank_name || '', // 个人用户开户行也存储在bank_name字段
        mobilePhone: user.phone || '',
        idCard: user.id_card || '',
        // 经纪人绑定信息
        brokerId: user.bound_broker_id || 0,
        brokerName: (user as any).bound_broker_name || '',
      });
    } else setEditForm({ ...emptyForm });
    setShowEdit(true);
  };

  /** 验证表单 */
  const validateForm = (): string | null => {
    const f = editForm;
    if (!f.username.trim()) return '登录账号必填';
    if (!editingUser && !f.password.trim()) return '新建用户时密码必填';
    if (!f.name.trim()) return '显示名称必填';
    if (f.cardType === '企业') {
      if (!f.companyName.trim()) return '企业名称必填';
      if (!f.bankAccountPublic.trim()) return '对公账户必填';
      if (!f.bankName.trim()) return '开户行名称必填';
      if (!f.contactName.trim()) return '联系人姓名必填';
      if (!f.contactPhone.trim()) return '联系电话必填';
    } else {
      if (!f.realName.trim()) return '姓名必填';
      if (!f.bankCard.trim()) return '银行卡号必填';
      if (!f.personalBankName.trim()) return '开户行必填';
      if (!f.mobilePhone.trim()) return '手机号码必填';
      if (!f.idCard.trim()) return '身份证号必填';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { showError(err); return; }
    setSaving(true);
    try {
      const f = editForm;
      const payload: any = {
        username: f.username, name: f.name, name_real: f.nameReal,
        role: f.role, card_type: f.cardType,
      };
      if (f.password) payload.password = f.password;
      if (f.cardType === '企业') {
        payload.company_name = f.companyName;
        payload.bank_account_public = f.bankAccountPublic;
        payload.bank_name = f.bankName;
        payload.name_real = f.contactName;
        payload.phone = f.contactPhone;
        // 个人字段置空
        payload.bank_card = null;
        payload.id_card = null;
      } else {
        payload.name_real = f.realName;
        payload.bank_card = f.bankCard;
        payload.bank_name = f.personalBankName; // 个人用户开户行也存储在bank_name字段
        payload.phone = f.mobilePhone;
        payload.id_card = f.idCard;
        // 企业字段置空
        payload.company_name = null;
        payload.bank_account_public = null;
      }
      // 保存绑定的经纪人信息
      payload.bound_broker_id = f.brokerId === 0 ? null : f.brokerId;
      payload.bound_broker_name = f.brokerId === 0 ? null : f.brokerName;

      if (editingUser) await userApi.update(editingUser.id, payload);
      else await userApi.create(payload);
      showSuccess(editingUser ? '更新成功' : '创建成功');
      setShowEdit(false); await loadUsers();
    } catch (err: any) { showError(err.message || '操作失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => { if (!confirm('确定删除?')) return; try { await userApi.delete(id); showSuccess('删除成功'); await loadUsers(); } catch { showError('删除失败'); } };

  const filteredUsers = useMemo(() => {
    let list = users;
    if (filterType === '个人') list = list.filter(u => { const ct=(u as any).card_type; return !ct || ct!=='企业' && ct!=='"企业"'; });
    else if (filterType === '企业') list = list.filter(u => { const ct=(u as any).card_type; return ct==='企业' || ct==='"企业"'; });
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter(u => (u.username||'').toLowerCase().includes(s) || (u.name||'').toLowerCase().includes(s) || ((u as any).company_name||'').toLowerCase().includes(s) || (u.phone||'').includes(s));
  }, [users, search, filterType]);

  const isEnterprise = editForm.cardType === '企业';
  const allBrokers = brokers; // 所有经纪人，不再区分类型

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><UserCog className="w-6 h-6 text-[#8B5CF6]" />用户管理</h1>
        <div className="flex gap-2">
          <button onClick={loadUsers} className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-all"><RefreshCw className="w-4 h-4" />刷新</button>
          <button onClick={() => openEdit(null)} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm transition-all"><Plus className="w-4 h-4" />新建用户</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{v:'all',l:'全部',c:users.length},
          {v:'个人',l:'个人',c:users.filter(u=>{const ct=(u as any).card_type; return !ct || ct!=='企业' && ct!=='"企业"';}).length},
          {v:'企业',l:'企业',c:users.filter(u=>{const ct=(u as any).card_type; return ct==='企业' || ct==='"企业"';}).length}].map(t=> (
          <button key={t.v} onClick={()=>setFilterType(t.v)} className={`px-3 py-1 rounded-full text-xs transition-all ${filterType===t.v?'bg-[#3B82F6] text-white':'bg-[#334155] text-[#94A3B8] hover:text-white'}`}>{t.l} ({t.c})</button>
        ))}
      </div>

      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" /><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="搜索用户名、姓名、企业名称或手机号..." className="w-full h-10 pl-10 pr-4 bg-[#1E293B] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6]" /></div>

      <div className="bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden">
        {loading?(<div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>)
        :filteredUsers.length===0?(<div className="text-center py-16 text-[#94A3B8]">{search||filterType!=='all'?'无匹配用户':'暂无用户'}</div>)
        :<div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-[#334155] bg-[#0F172A]">
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">账号</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">类型</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">姓名/企业</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">角色</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">绑定经纪人</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">手机</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">身份证号/联系人</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">银行卡/对公账户</th>
            <th className="px-4 py-3 text-left text-xs text-[#94A3B8] font-medium">开户行</th>
            <th className="px-4 py-3 text-right text-xs text-[#94A3B8] font-medium">操作</th>
          </tr></thead>
          <tbody className="divide-y divide-[#334155]/50">
            {filteredUsers.map(u=>{
              const isEnt=(u as any).card_type==='企业';
              return (
                <tr key={u.id} className="hover:bg-[#334155]/20 transition-colors">
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{u.username}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${isEnt?'bg-amber-500/10 text-amber-400':'bg-emerald-500/10 text-emerald-400'}`}>{isEnt?<Building2 className="w-3 h-3" />:<UserIcon className="w-3 h-3" />}{(u as any).card_type||'个人'}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap">{isEnt?<div><span className="text-[#F1F5F9]">{(u as any).company_name||'—'}</span>{(u as any).name_real&&<span className="ml-1 text-[#64748B] text-xs">({(u as any).name_real})</span>}</div>:<span>{(u as any).name_real||u.name||'—'}</span>}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className={`px-2 py-0.5 rounded text-xs ${u.role==='admin'?'bg-purple-500/10 text-purple-400':'bg-blue-500/10 text-blue-400'}`}>{u.role || '经纪人'}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs">{(u as any).bound_broker_name?<span className="text-blue-400">{(u as any).bound_broker_name}</span>:<span className="text-[#64748B]">—</span>}</td>
                  <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap text-xs">{u.phone||'—'}</td>
                  <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap text-xs font-mono">{isEnt?((u as any).name_real||'—'):((u as any).id_card||'—')}</td>
                  <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap text-xs font-mono">{isEnt?((u as any).bank_account_public||'—'):((u as any).bank_card||'—')}</td>
                  <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap text-xs">{(u as any).bank_name||'—'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap"><div className="flex justify-end gap-1"><button onClick={()=>openEdit(u)} className="p-1.5 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded"><Pencil className="w-4 h-4" /></button><button onClick={()=>handleDelete(u.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button></div></td>
                </tr>
              );
            })}
          </tbody>
        </table></div>}
      </div>

      {/* 编辑/新建弹窗 */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-bold">{editingUser?'编辑用户':'新建用户'}</h3><button onClick={()=>setShowEdit(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button></div>
            <div className="space-y-4">
              {/* 类型选择 */}
              <div>
                <label className="text-xs text-[#94A3B8] mb-2 block">账户类型 {!editingUser&&<span className="text-red-400">*</span>}</label>
                <div className="flex gap-3">
                  {(['个人','企业'] as const).map(t=>(
                    <button key={t} type="button" onClick={()=>{if(!editingUser)setEditForm({...editForm,cardType:t})}} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border transition-all ${editForm.cardType===t?'border-[#3B82F6] bg-[#3B82F6]/10 text-[#3B82F6]':'border-[#334155] bg-[#0F172A] text-[#94A3B8]'} ${editingUser?'opacity-50 cursor-not-allowed':''}`}>
                      {t==='企业'?<Building2 className="w-4 h-4" />:<UserIcon className="w-4 h-4" />}{t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">登录账号 {!editingUser&&<span className="text-red-400">*</span>}</label><input value={editForm.username} onChange={e=>setEditForm({...editForm,username:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="登录账号" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">登录密码 {!editingUser&&<span className="text-red-400">*</span>}{editingUser&&<span className="text-[#64748B]">(留空不改)</span>}</label><input type="password" value={editForm.password} onChange={e=>setEditForm({...editForm,password:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder={editingUser?'留空不修改':'登录密码'} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">显示名称 <span className="text-red-400">*</span></label><input value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="系统中显示的名称" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">角色</label><select value={editForm.role} onChange={e=>setEditForm({...editForm,role:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
                      {roles.length===0?(
                        <><option value="admin">管理员</option><option value="broker">经纪人</option></>
                      ):roles.map(r=>(
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                    </select></div>
              </div>

              {/* 企业字段 */}
              {isEnterprise?(
                <div className="pt-3 border-t border-[#334155] space-y-3">
                  <h4 className="text-xs font-medium text-amber-400 flex items-center gap-1"><Building2 className="w-3 h-3" />企业信息（必填）</h4>
                  <div><label className="text-xs text-[#94A3B8] mb-1.5 block">企业名称 <span className="text-red-400">*</span></label><input value={editForm.companyName} onChange={e=>setEditForm({...editForm,companyName:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none text-[#F1F5F9]" placeholder="企业全称" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">对公账户 <span className="text-red-400">*</span></label><input value={editForm.bankAccountPublic} onChange={e=>setEditForm({...editForm,bankAccountPublic:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none text-[#F1F5F9]" placeholder="对公银行账户" /></div>
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">开户行名称 <span className="text-red-400">*</span></label><input value={editForm.bankName} onChange={e=>setEditForm({...editForm,bankName:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none text-[#F1F5F9]" placeholder="开户行全称" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">联系人姓名 <span className="text-red-400">*</span></label><input value={editForm.contactName} onChange={e=>setEditForm({...editForm,contactName:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none text-[#F1F5F9]" placeholder="联系人姓名" /></div>
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">联系电话 <span className="text-red-400">*</span></label><input value={editForm.contactPhone} onChange={e=>setEditForm({...editForm,contactPhone:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#F59E0B] focus:outline-none text-[#F1F5F9]" placeholder="联系电话" /></div>
                  </div>
                </div>
              ):(
                /* 个人字段 */
                <div className="pt-3 border-t border-[#334155] space-y-3">
                  <h4 className="text-xs font-medium text-emerald-400 flex items-center gap-1"><UserIcon className="w-3 h-3" />个人信息（必填）</h4>
                  <div><label className="text-xs text-[#94A3B8] mb-1.5 block">姓名 <span className="text-red-400">*</span></label><input value={editForm.realName} onChange={e=>setEditForm({...editForm,realName:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#10B981] focus:outline-none text-[#F1F5F9]" placeholder="真实姓名" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">银行卡号 <span className="text-red-400">*</span></label><input value={editForm.bankCard} onChange={e=>setEditForm({...editForm,bankCard:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#10B981] focus:outline-none text-[#F1F5F9]" placeholder="银行卡号" /></div>
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">开户行 <span className="text-red-400">*</span></label><input value={editForm.personalBankName} onChange={e=>setEditForm({...editForm,personalBankName:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#10B981] focus:outline-none text-[#F1F5F9]" placeholder="开户行名称" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">手机号码 <span className="text-red-400">*</span></label><input value={editForm.mobilePhone} onChange={e=>setEditForm({...editForm,mobilePhone:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#10B981] focus:outline-none text-[#F1F5F9]" placeholder="手机号" /></div>
                    <div><label className="text-xs text-[#94A3B8] mb-1.5 block">身份证号 <span className="text-red-400">*</span></label><input value={editForm.idCard} onChange={e=>setEditForm({...editForm,idCard:e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#10B981] focus:outline-none text-[#F1F5F9]" placeholder="身份证号码" /></div>
                  </div>
                </div>
              )}

              {/* 经纪人绑定 */}
              <div className="pt-3 border-t border-[#334155]">
                <h4 className="text-xs font-medium text-blue-400 mb-3 flex items-center gap-1"><UsersIcon className="w-3 h-3" />经纪人绑定</h4>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1.5 block">绑定经纪人</label>
                  <select 
                    value={editForm.brokerId} 
                    onChange={e=>{const b=allBrokers.find(x=>x.id===Number(e.target.value));setEditForm({...editForm,brokerId:Number(e.target.value),brokerName:b?.name||''})}} 
                    disabled={!isAdmin} // 只有管理员可以编辑
                    className={`w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9] ${!isAdmin ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    <option value={0}>不绑定</option>{allBrokers.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-4 mt-4 border-t border-[#334155]">
              <button onClick={()=>setShowEdit(false)} className="flex-1 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334155]/30">取消</button>
              <button onClick={handleSave} disabled={saving} className={`flex-1 py-2.5 rounded-lg text-sm disabled:opacity-50 text-white ${isEnterprise?'bg-amber-500 hover:bg-amber-600':'bg-emerald-500 hover:bg-emerald-600'}`}>{saving?'保存中...':`保存${isEnterprise?'企业':'个人'}用户`}</button>
            </div>
          </div>
        </div>
      )}
      {toast&&<div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type==='success'?'bg-emerald-500':'bg-red-500'} text-white`}>{toast.message}</div>}
    </div>
  );
}
